// BIDashboardPage - analyst-grade cross-domain BI view.
// Sidebar - Assurance - BI Dashboard. Available to any role with analytics.view.
import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell,
} from 'recharts';
import {
  BarChart3, TrendingUp, AlertTriangle, ShieldAlert, ClipboardList,
  Target, RefreshCw, Loader2, Download, Activity,
} from 'lucide-react';
import { toast } from 'sonner';
import AskTheDataChip from '@/components/AskTheDataChip';

const RANGE_OPTIONS = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 180, label: '6 months' },
  { days: 365, label: '1 year' },
];

const LIKELIHOOD_ORDER = ['very_low', 'low', 'medium', 'high', 'very_high'];
const SEVERITY_ORDER = ['negligible', 'minor', 'moderate', 'major', 'catastrophic'];

const HEAT_COLORS = ['#0f172a', '#0e4a6d', '#0e6a89', '#0a97a3', '#e5c66b', '#e0851a', '#c1364c', '#8a1128'];

function heatColor(value, maxValue) {
  if (!maxValue) return HEAT_COLORS[0];
  const ratio = value / maxValue;
  const idx = Math.min(HEAT_COLORS.length - 1, Math.floor(ratio * HEAT_COLORS.length));
  return HEAT_COLORS[idx];
}

function StatCard({ icon: Icon, label, value, sub, tone = 'cyan', testid }) {
  const tones = {
    cyan: 'from-cyan-500/10 to-slate-900 border-cyan-500/30 text-cyan-300',
    amber: 'from-amber-500/10 to-slate-900 border-amber-500/30 text-amber-300',
    rose: 'from-rose-500/10 to-slate-900 border-rose-500/30 text-rose-300',
    emerald: 'from-emerald-500/10 to-slate-900 border-emerald-500/30 text-emerald-300',
    violet: 'from-violet-500/10 to-slate-900 border-violet-500/30 text-violet-300',
  };
  return (
    <div
      className={'rounded-lg border p-4 bg-gradient-to-br ' + (tones[tone] || tones.cyan)}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide opacity-80">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="text-3xl font-semibold text-white mt-2">{value}</div>
      {sub ? <div className="text-[11px] text-slate-400 mt-1">{sub}</div> : null}
    </div>
  );
}

export default function BIDashboardPage() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (windowDays = days) => {
    try {
      const r = await api.get('/bi/overview', { params: { days: windowDays } });
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load BI overview');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(days); }, [days]);

  const risk5x5 = useMemo(() => {
    if (!data) return { grid: [], max: 0 };
    const grid = LIKELIHOOD_ORDER.map((lk) =>
      SEVERITY_ORDER.map((sv) => {
        const found = (data.risk_matrix || []).find((m) => m.likelihood === lk && m.severity === sv);
        return { likelihood: lk, severity: sv, count: found ? found.count : 0 };
      })
    );
    const max = Math.max(0, ...grid.flat().map((c) => c.count));
    return { grid, max };
  }, [data]);

  const capaPie = useMemo(() => {
    if (!data || !data.capa_effectiveness) return [];
    const e = data.capa_effectiveness;
    return [
      { name: 'On-time', value: e.on_time || 0, color: '#10b981' },
      { name: 'Late', value: e.late || 0, color: '#f43f5e' },
    ];
  }, [data]);

  const exportCsv = () => {
    if (!data || !data.trend) return;
    const rows = [
      ['label', 'incidents', 'hazards', 'capas_closed'],
      ...data.trend.map((t) => [t.label, t.incidents, t.hazards, t.capas_closed]),
    ];
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'aeroguard_bi_trend_' + days + 'd.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    toast.success('Trend data downloaded');
  };

  const refresh = () => {
    setRefreshing(true);
    load(days);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading BI overview…
      </div>
    );
  }
  if (!data) return null;

  const t = data.totals || {};

  return (
    <div className="p-6 space-y-6" data-testid="bi-dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-cyan-400" />
            BI Dashboard
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Cross-domain safety metrics — trends, incident categories, 5×5 risk matrix, CAPA effectiveness.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center rounded-full border border-slate-700 overflow-hidden" role="group" aria-label="Time range" data-testid="bi-range-toggle">
            {RANGE_OPTIONS.map((o) => (
              <button
                key={o.days}
                type="button"
                onClick={() => setDays(o.days)}
                className={
                  'px-3 py-1 text-xs transition-colors ' +
                  (days === o.days ? 'bg-cyan-500/25 text-cyan-100' : 'bg-slate-900 text-slate-400 hover:text-slate-200')
                }
                data-testid={'bi-range-' + o.days}
                aria-pressed={days === o.days}
              >
                {o.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            className="border-slate-700 text-slate-200"
            data-testid="bi-export-csv"
          >
            <Download className="w-4 h-4 mr-2" /> CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            disabled={refreshing}
            className="border-slate-700 text-slate-200"
            data-testid="bi-refresh"
          >
            <RefreshCw className={'w-4 h-4 mr-2 ' + (refreshing ? 'animate-spin' : '')} /> Refresh
          </Button>
          <AskTheDataChip overview={data} />
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard icon={AlertTriangle} label="Incidents (window)" value={t.incidents?.window ?? 0}
          sub={(t.incidents?.open ?? 0) + ' open, ' + (t.incidents?.all ?? 0) + ' total'}
          tone="rose" testid="bi-kpi-incidents" />
        <StatCard icon={Activity} label="Hazards" value={t.hazards?.window ?? 0}
          sub={(t.hazards?.all ?? 0) + ' total'} tone="amber" testid="bi-kpi-hazards" />
        <StatCard icon={ShieldAlert} label="Risks (High+)" value={t.risks?.high_plus ?? 0}
          sub={(t.risks?.all ?? 0) + ' total risks'} tone="violet" testid="bi-kpi-risks" />
        <StatCard icon={ClipboardList} label="CAPAs open" value={t.capas?.open ?? 0}
          sub={(t.capas?.closed_in_window ?? 0) + ' closed in window'} tone="cyan" testid="bi-kpi-capas" />
        <StatCard icon={Target} label="Findings open" value={t.findings?.open ?? 0}
          sub={(t.findings?.all ?? 0) + ' total'} tone="emerald" testid="bi-kpi-findings" />
      </div>

      {/* Trends line chart */}
      <Card className="bg-slate-900/60 border-slate-800" data-testid="bi-trend-card">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-cyan-400" /> Trend — {days} day window
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.trend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="label" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="incidents" stroke="#f43f5e" strokeWidth={2} dot={false} name="Incidents" />
              <Line type="monotone" dataKey="hazards" stroke="#f59e0b" strokeWidth={2} dot={false} name="Hazards" />
              <Line type="monotone" dataKey="capas_closed" stroke="#10b981" strokeWidth={2} dot={false} name="CAPAs closed" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Categories bar + CAPA effectiveness donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 bg-slate-900/60 border-slate-800" data-testid="bi-categories-card">
          <CardHeader>
            <CardTitle className="text-white text-base">Top incident categories</CardTitle>
          </CardHeader>
          <CardContent>
            {(data.categories || []).length === 0 ? (
              <div className="text-slate-500 text-sm py-6 text-center">
                No incident data in the selected window.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.categories} layout="vertical" margin={{ left: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis type="number" stroke="#64748b" fontSize={11} allowDecimals={false} />
                  <YAxis type="category" dataKey="category" stroke="#64748b" fontSize={11} width={110} />
                  <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6 }} />
                  <Bar dataKey="count" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="bg-slate-900/60 border-slate-800" data-testid="bi-capa-effectiveness-card">
          <CardHeader>
            <CardTitle className="text-white text-base">CAPA effectiveness</CardTitle>
          </CardHeader>
          <CardContent>
            {capaPie.reduce((a, b) => a + b.value, 0) === 0 ? (
              <div className="text-slate-500 text-sm py-6 text-center">
                No CAPAs closed in this window.
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={capaPie} innerRadius={45} outerRadius={80} dataKey="value" nameKey="name" paddingAngle={2}>
                      {capaPie.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="text-center text-slate-300 text-sm mt-2">
                  On-time rate:{' '}
                  <span className="text-emerald-400 font-semibold" data-testid="bi-capa-rate">
                    {data.capa_effectiveness?.rate != null
                      ? Math.round(data.capa_effectiveness.rate * 100) + '%'
                      : '—'}
                  </span>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Risk 5×5 heatmap */}
      <Card className="bg-slate-900/60 border-slate-800" data-testid="bi-risk-matrix-card">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-violet-400" /> Risk matrix (5×5)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="text-xs w-full">
              <thead>
                <tr>
                  <th className="p-2 text-left text-slate-500 uppercase tracking-wide">Likelihood ↓ / Severity →</th>
                  {SEVERITY_ORDER.map((s) => (
                    <th key={s} className="p-2 text-slate-400 capitalize font-normal">{s.replace('_', ' ')}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {risk5x5.grid.map((row, ri) => (
                  <tr key={LIKELIHOOD_ORDER[ri]}>
                    <td className="p-2 text-slate-400 capitalize font-normal">
                      {LIKELIHOOD_ORDER[ri].replace('_', ' ')}
                    </td>
                    {row.map((cell) => (
                      <td key={cell.likelihood + cell.severity} className="p-1">
                        <div
                          className="rounded flex items-center justify-center h-11 font-semibold text-white"
                          style={{ background: heatColor(cell.count, risk5x5.max) }}
                          title={cell.likelihood + ' × ' + cell.severity + ' — ' + cell.count + ' risks'}
                        >
                          {cell.count}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Top findings */}
      {(data.top_findings || []).length > 0 && (
        <Card className="bg-slate-900/60 border-slate-800" data-testid="bi-findings-card">
          <CardHeader>
            <CardTitle className="text-white text-base">Recent top findings</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1">
              {data.top_findings.map((f, i) => (
                <li key={i} className="flex items-center justify-between text-sm text-slate-200 border-b border-slate-800 py-2">
                  <span className="truncate flex-1">{f.title || 'Untitled finding'}</span>
                  {f.severity ? <Badge variant="outline" className="ml-2 text-[10px]">{f.severity}</Badge> : null}
                  {f.status ? <span className="text-slate-500 text-[10px] ml-3">{f.status}</span> : null}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="text-[10px] text-slate-500 text-right">
        Data refreshed {new Date(data.generated_at).toLocaleString()}
      </div>
    </div>
  );
}
